"use client";

import { useCallback, useState } from "react";
import { Check, Copy } from "lucide-react";

import { cn } from "@/lib/utils";
import { CodeBlock } from "@/components/code/CodeBlock";
import { getLanguageDisplayName } from "@/lib/code/language";

type MarkdownCodeBlockProps = {
  code: string;
  language?: string;
};

export function MarkdownCodeBlock({ code, language }: MarkdownCodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const languageLabel = getLanguageDisplayName(language);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [code]);

  return (
    <div className="w-full max-w-full min-w-0 overflow-hidden rounded-[var(--radius-xxl)] border border-border/70 bg-[color-mix(in_oklab,var(--token-surface-muted)_90%,transparent)] shadow-[var(--shadow-sm)]">
      <div className="flex items-center justify-between border-b border-border/40 bg-background/60 px-3 py-2 text-[11px] uppercase tracking-[0.32em] text-muted-foreground/80">
        <span>{languageLabel}</span>
        <button
          type="button"
          onClick={handleCopy}
          className={cn(
            "inline-flex items-center gap-1 rounded-[var(--radius-full)] border border-border/50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] transition",
            copied ? "bg-primary/10 text-primary" : "hover:bg-muted/40 hover:text-foreground",
          )}
        >
          {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <CodeBlock
        code={code}
        language={language}
        lineNumbers
        maxHeight={540}
        className="rounded-none border-0 border-t border-border/40 bg-transparent"
      />
    </div>
  );
}
